import {
  handleForbiddenException,
  handleNetworkErrorException,
  handleUnauthorizedException,
} from '@/http/exceptions'
import { AxiosError } from 'axios'

import { httpClient } from './httpClient'
import { HttpPayload } from './types'

interface HandleHttpErrorOptions {}

export async function performHttpRequest(httpPayload: HttpPayload) {
  const maxRetries = 1
  const initialDelay = 1000
  let attempt = 0

  while (true) {
    try {
      const response = await httpClient[httpPayload?.method]({
        url: httpPayload?.url,
        version: httpPayload?.version,
        useBearerToken: httpPayload?.useBearerToken,
        bearerToken: httpPayload?.bearerToken,
        params: httpPayload?.params,
        data: httpPayload?.body,
        headers: httpPayload?.headers,
        multipart: httpPayload?.multipart,
        timeout: httpPayload?.timeout,
        responseType: httpPayload?.responseType,
        onUploadProgress: httpPayload?.onUploadProgress,
      })

      // Unwrap the ApiResponse wrapper if present
      // Backend returns: { success: true, message: "...", data: {...} }
      // We extract and return the 'data' field to make it transparent
      if (
        response?.data &&
        typeof response.data === 'object' &&
        'success' in response.data &&
        'data' in response.data
      ) {
        if (response.data.success === false) {
          const isRateLimit =
            response.data.message?.includes('Global User Limit exceeded') ||
            response.data.message?.includes('ERR05') ||
            response.data.message?.includes('Too Many Requests')

          if (isRateLimit && attempt < maxRetries) {
            attempt++
            const delay = initialDelay * Math.pow(2, attempt - 1)
            console.warn(`Rate limit hit (ERR05) in response. Retrying attempt ${attempt}/${maxRetries} after ${delay}ms...`)
            await new Promise((resolve) => setTimeout(resolve, delay))
            continue
          }
        }

        return {
          ...response,
          data: response.data.data, // Extract the actual data
          _apiMessage: response.data.message, // Preserve message for debugging
          _apiSuccess: response.data.success, // Preserve success flag
        }
      }

      return response
    } catch (error: any) {
      const isRateLimit =
        error?.response?.status === 429 ||
        error?.response?.data?.message?.includes('Global User Limit exceeded') ||
        error?.response?.data?.message?.includes('ERR05') ||
        error?.message?.includes('ERR05')

      if (isRateLimit && attempt < maxRetries) {
        attempt++
        const delay = initialDelay * Math.pow(2, attempt - 1)
        console.warn(`Rate limit hit (ERR05) in catch. Retrying attempt ${attempt}/${maxRetries} after ${delay}ms...`)
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      throw error
    }
  }
}

export function handleHttpError(e: AxiosError, options?: HandleHttpErrorOptions) {
  // Check if the response has success: false (API-level error)
  if (e?.response?.data && typeof e.response.data === 'object' && 'success' in e.response.data) {
    const apiResponse = e.response.data as any
    if (apiResponse.success === false) {
      // API returned an error in the response
      // This is handled by the caller, just return
      return { handled: false, error: e }
    }
  }

  if (e?.response) {
    if (e?.response?.status === 401) {
      handleUnauthorizedException(e)
      return { handled: true }
    }

    if (e?.response?.status === 403) {
      handleForbiddenException(e)
      return { handled: true }
    }
  } else {
    handleNetworkErrorException(e)
  }

  return { handled: false, error: e }
}
